import { useTranslation } from 'react-i18next';
import { Popover, Typography } from 'antd';
import { useGetAdditionalCosts } from 'api/additional-cost/use-get-additional-costs';
import type { TAdditionalCostType } from 'api/swagger';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard';
import { round100 } from 'shared/utils/numbers/round';
import { AdditionalCostType } from 'types/additional-cost';
import { CURRENCY_SIGN, ECurrency } from 'types/currencies';
import { StatisticMoney } from 'components/common/statistic-money/statistic-money';
import styles from './additional-costs-statistic.module.scss';
import type { IAdditionalCostsStatisticProps } from './types';

export const AdditionalCostsStatisticComponent = (props: IAdditionalCostsStatisticProps) => {
  const { orderId } = props;
  const { data: additionalCosts } = useGetAdditionalCosts({ orderId });
  const { t } = useTranslation('additional-cost');

  if (!additionalCosts) {
    return null;
  }

  let additionalCostsSum = 0;
  const additionalCostsMap: Record<TAdditionalCostType, number> = {
    customs_cost: 0,
    warehouse_cost: 0,
    clients_mistake: 0,
    partners_warehouse_cost: 0,
    fine: 0,
    other: 0,
  };

  additionalCosts?.forEach((cost) => {
    additionalCostsMap[cost.type] += cost.valueInDefault;
    additionalCostsSum += cost.valueInDefault;
  });

  const additionalCostEntries = Object.entries(additionalCostsMap) as [TAdditionalCostType, number][];

  const content = (
    <div>
      {additionalCostEntries.map(([key, value]) => (
        <div key={key} className={styles.item}>
          <Typography.Paragraph strong>{AdditionalCostType[key as TAdditionalCostType]}: </Typography.Paragraph>
          <Typography.Paragraph>
            {round100(value)} {CURRENCY_SIGN[ECurrency.EUR]}
          </Typography.Paragraph>
        </div>
      ))}
    </div>
  );

  return (
    <Popover content={content} trigger='click' placement='bottomLeft'>
      <button className={styles.popoverTrigger}>
        <StatisticMoney title={t('additional_cost')} currency={ECurrency.EUR} value={round100(additionalCostsSum)} />
      </button>
    </Popover>
  );
};

export const AdditionalCostsStatistic = withRoleGuard<IAdditionalCostsStatisticProps>(
  AdditionalCostsStatisticComponent,
  [
    UserRole.admin,
    UserRole.assistant,
    UserRole.transport_manager,
    UserRole.customer_manager,
    UserRole.storekeeper,
    UserRole.accountant,
  ],
);
